import React, {useEffect, useState} from "react";
import {createPortal} from "react-dom";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCheck, faChevronDown, faXmark} from "@fortawesome/free-solid-svg-icons";

interface Props {
    value: string;
    onChange: (value: string) => void;
    availableYears?: Array<number>;
    startYear?: number;
    ariaLabel?: string;
}

function getYearOptions(startYear: number, availableYears?: Array<number>): Array<number> {
    if (availableYears) {
        const years = Array.from(new Set(availableYears))
            .filter((year) => year >= startYear)
            .sort((a, b) => b - a);
        years.push(1);
        return years;
    }

    const currentYear = new Date().getFullYear();
    const years: Array<number> = [];
    for (let year = currentYear; year >= startYear; year--) {
        years.push(year);
    }
    years.push(1);
    return years;
}

function V2YearPicker(props: Props) {
    const [open, setOpen] = useState<boolean>(false);
    const options = getYearOptions(props.startYear ?? 2023, props.availableYears);
    const label = props.value === "1" ? "Total" : props.value;
    const ariaLabel = props.ariaLabel ?? "Select year";

    useEffect(() => {
        if (!open) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                setOpen(false);
            }
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [open]);

    return (
        <>
            <button
                aria-expanded={open}
                aria-haspopup="dialog"
                aria-label={ariaLabel}
                className="v2-year-selector"
                onClick={() => setOpen(true)}
                type="button"
            >
                <span>{label}</span>
                <FontAwesomeIcon icon={faChevronDown} />
            </button>
            {open && createPortal(
                <div
                    className="v2-year-picker-backdrop"
                    onMouseDown={(event) => {
                        if (event.target === event.currentTarget) setOpen(false);
                    }}
                    role="presentation"
                >
                    <div aria-label={ariaLabel} aria-modal="true" className="v2-year-picker" role="dialog">
                        <div className="v2-year-picker-header">
                            <h2>Select year</h2>
                            <button
                                aria-label="Close year selector"
                                className="v2-year-picker-close"
                                onClick={() => setOpen(false)}
                                type="button"
                            >
                                <FontAwesomeIcon icon={faXmark} />
                            </button>
                        </div>
                        <div className="v2-year-picker-options">
                            {options.map((year) => {
                                const yearValue = String(year);
                                const isSelected = props.value === yearValue;
                                return (
                                    <button
                                        aria-pressed={isSelected}
                                        className={`v2-year-picker-option${isSelected ? " active" : ""}`}
                                        key={yearValue}
                                        onClick={() => {
                                            props.onChange(yearValue);
                                            setOpen(false);
                                        }}
                                        type="button"
                                    >
                                        <span>{year === 1 ? "Total" : year}</span>
                                        {isSelected && <FontAwesomeIcon icon={faCheck} />}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
}

export default V2YearPicker;
